import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FileText, MessageSquare, Phone, ShieldOff } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { DetailCard } from '@/components/molecules';
import { Fullbleed } from '@/contexts/fullbleed';
import { SmsNotificationRules } from '@/components/settings/SmsNotificationRules';
import { messagingApi } from '@/lib/messaging/api';
import { formatPhoneDisplay } from '@/lib/contacts/phone';

// The legacy SMS config endpoints (templates, opt-out handling) lived on
// the now-defunct monolith — `/api/settings/sms`,
// `/api/settings/sms/templates`, … — and operations has no equivalent
// yet. The page previously made all 4 of those calls on mount, got 404s,
// and rendered "No SMS numbers" because the failed config call left the
// list empty. Keep the working slice (phone-numbers from
// `/api/messaging`, the same source the inbox uses) and surface a
// "coming soon" placeholder for templates / opt-out so users see what's
// here today rather than a broken edit screen.
export const Route = createFileRoute('/_dashboard/dashboard/settings/sms')({
  component: SmsSettingsPage,
});

function SmsSettingsPage() {
  const { data: numbers = [], isLoading } = useQuery({
    queryKey: ['messaging', 'phone-numbers', 'all'],
    queryFn: () => messagingApi.phoneNumbers.listAll(),
  });

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">SMS</h1>
            <p className="text-xs text-muted-foreground">
              Provisioned SMS-capable phone numbers for your account.
            </p>
          </div>
        }
      >
        <div className="space-y-4">
          <DetailCard label="Phone Numbers" icon={Phone}>
            {isLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : numbers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No SMS numbers provisioned yet.</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border">
                {numbers.map((num) => (
                  <li key={num.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {formatPhoneDisplay(num.e164)}
                        </p>
                        {num.label ? (
                          <p className="truncate text-xs text-muted-foreground">{num.label}</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {!num.smsEnabled && (
                        <Badge variant="outline" className="text-[10px]">
                          SMS off
                        </Badge>
                      )}
                      {num.mmsEnabled && (
                        <Badge variant="secondary" className="text-[10px]">
                          MMS
                        </Badge>
                      )}
                      <Badge
                        variant={num.status === 'ACTIVE' ? 'default' : 'outline'}
                        className="text-[10px]"
                      >
                        {String(num.status).toLowerCase()}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DetailCard>

          <SmsNotificationRules numbers={numbers} />

          <DetailCard label="Templates" icon={FileText}>
            <p className="text-sm text-muted-foreground">
              SMS templates are being rebuilt on the operations backend. Coming soon.
            </p>
          </DetailCard>

          <DetailCard label="Opt-out Handling" icon={ShieldOff}>
            <p className="text-sm text-muted-foreground">
              Opt-out message and double opt-in configuration are being rebuilt on the operations
              backend. Coming soon.
            </p>
          </DetailCard>
        </div>
      </DetailPageLayout>
    </>
  );
}
